// Pure helpers for editing a project's location tier structure
// (tierDefinitions + master tree). Shared by LocationStructureScreen (the
// tier-admin UI) and, potentially, an inline "add a new value" affordance
// inside the Upload Location picker itself. Every function returns a new
// project object (never mutates the one passed in) so callers can save it
// straight through the existing POST /api/state upsertProject call.
import { uid } from './uid';

function cloneTree(master) {
  return JSON.parse(JSON.stringify(Array.isArray(master) ? master : []));
}

// parentIds: node ids from root to the parent whose children list we want.
// [] means the root list (master itself, i.e. tier 0).
function findNodeList(master, parentIds) {
  if (!parentIds.length) return master;
  let list = master;
  for (const id of parentIds) {
    const node = (list || []).find((n) => n.id === id);
    if (!node) return null;
    if (!Array.isArray(node.children)) node.children = [];
    list = node.children;
  }
  return list;
}

export function addTier(project, name) {
  const tierDefinitions = Array.isArray(project.tierDefinitions) ? project.tierDefinitions : [];
  const nextOrder = tierDefinitions.length;
  const newTier = { id: uid('tier'), name: (name || '').trim() || `Tier ${nextOrder + 1}`, order: nextOrder };
  return { ...project, tierDefinitions: [...tierDefinitions, newTier] };
}

export function renameTier(project, tierId, name) {
  const trimmed = (name || '').trim();
  if (!trimmed) return project;
  const tierDefinitions = (Array.isArray(project.tierDefinitions) ? project.tierDefinitions : []).map((t) =>
    t.id === tierId ? { ...t, name: trimmed } : t
  );
  return { ...project, tierDefinitions };
}

export function removeTier(project, tierId) {
  const tierDefinitions = (Array.isArray(project.tierDefinitions) ? project.tierDefinitions : []).filter((t) => t.id !== tierId);
  return { ...project, tierDefinitions };
}

// parentIds: node ids from root down to the parent this new value should be
// added under. [] adds a new root (tier 0) value.
export function addValueAtPath(project, parentIds, name) {
  const trimmed = (name || '').trim();
  if (!trimmed) return project;
  const master = cloneTree(project.master);
  const list = findNodeList(master, parentIds);
  if (!list) return project;
  const tierDefinitions = Array.isArray(project.tierDefinitions) ? project.tierDefinitions : [];
  const tier = tierDefinitions[parentIds.length];
  list.push({
    id: uid('node'),
    tierId: tier?.id,
    name: trimmed,
    comments: [],
    uploads: [],
    children: [],
  });
  return { ...project, master };
}

// nodeIds: full path from root to the node being renamed/removed (its own id last).
export function renameValueAtPath(project, nodeIds, name) {
  const trimmed = (name || '').trim();
  if (!trimmed || !nodeIds.length) return project;
  const master = cloneTree(project.master);
  const list = findNodeList(master, nodeIds.slice(0, -1));
  const node = list?.find((n) => n.id === nodeIds[nodeIds.length - 1]);
  if (!node) return project;
  node.name = trimmed;
  return { ...project, master };
}

export function removeValueAtPath(project, nodeIds) {
  if (!nodeIds.length) return project;
  const master = cloneTree(project.master);
  const list = findNodeList(master, nodeIds.slice(0, -1));
  if (!list) return project;
  const index = list.findIndex((n) => n.id === nodeIds[nodeIds.length - 1]);
  if (index === -1) return project;
  list.splice(index, 1);
  return { ...project, master };
}

// Packages/trades (project.responsibilities) - separate from the tier tree
// (a package is who's doing the work, not a location), but edited from the
// same admin screen since both are configured before uploading can happen.
export function addPackage(project, { name, supplier }) {
  const trimmedName = (name || '').trim();
  if (!trimmedName) return project;
  const responsibilities = Array.isArray(project.responsibilities) ? project.responsibilities : [];
  const newPackage = {
    id: uid('resp'),
    name: trimmedName,
    supplier: (supplier || '').trim(),
    supplierCode: '',
  };
  return { ...project, responsibilities: [...responsibilities, newPackage] };
}

export function removePackage(project, packageId) {
  const responsibilities = (Array.isArray(project.responsibilities) ? project.responsibilities : []).filter((p) => p.id !== packageId);
  return { ...project, responsibilities };
}
